import React, { useState, useMemo, useCallback } from 'react';
import { SearchTextField } from 'src/components/BeaconV2Components/SearchInput/SearchInputBar';
import { SlColumn, SlRow, useStacklineTheme } from '@stackline/ui';
import { useAppSelector, useDebouncedValue } from 'src/utils/Hooks';
import SegmentCardGrid from './SegmentCardGrid';
import Box from '@mui/material/Box';
import { NoDataPlaceholderWithChildren } from 'src/components/BeaconV2Components/ExperimentalLayout/Segments/NoDataPlaceholder';
import { Link } from 'react-router-dom';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { RightArrow } from 'src/components/SvgIcons';

export const CreateNewSegmentLink = ({
  text = 'Create a segment',
  sx = {}
}: {
  text?: string;
  fontWeight?: number;
  sx?: React.CSSProperties;
}) => {
  const { searchParams } = useAppSelector((state) => state.app.queryParams);
  return (
    <Link to={`/search${searchParams}&entityType=segment&action=create`}>
      <Text sx={{ ...{ fontWeight: 500 }, ...sx }}>
        {text}
        <RightArrow
          style={{
            width: '12px',
            height: '12px',
            marginLeft: '4px',
            position: 'relative',
            top: '2px'
          }}
        />
      </Text>
    </Link>
  );
};

const NoDataPlaceholder = ({ team }: { team: boolean }) => {
  return (
    <Box sx={{ marginTop: '80px' }}>
      <NoDataPlaceholderWithChildren
        title="Add segment"
        subtitle={`There are currently no segments to display. All ${
          team ? 'team' : 'your'
        } segments will be available on this page.`}
      >
        <CreateNewSegmentLink />
      </NoDataPlaceholderWithChildren>
    </Box>
  );
};

interface SegmentsViewTabProps {
  /**
   * True if we are viewing team segments
   */
  team?: boolean;
  /**
   * Array of segment IDs that are currently being processed
   */
  processingSegments?: string[];
}

/**
 * Segments tab on the segments page
 */
const SegmentsViewTab = ({ team, processingSegments = [] }: SegmentsViewTabProps) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 500);

  const theme = useStacklineTheme();
  const mySegments = useAppSelector((state) => state.segments.mySegments);
  const teamSegments = useAppSelector((state) => state.segments.teamSegments);

  // Cache segments with pre-computed searchable text
  const segmentsWithSearchText = useMemo(() => {
    const segments = team ? teamSegments : mySegments;
    return segments.map((segment) => ({
      ...segment,
      searchText:
        `${segment.displayName} ${segment.segment.owner.firstName} ${segment.segment.owner.lastName}`.toLowerCase()
    }));
  }, [mySegments, team, teamSegments]);

  const filteredSegments = useMemo(() => {
    if (!debouncedSearch) {
      return segmentsWithSearchText;
    }

    const searchLower = debouncedSearch.toLowerCase();
    return segmentsWithSearchText.filter((segment) => segment.searchText.includes(searchLower));
  }, [debouncedSearch, segmentsWithSearchText]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  }, []);

  return (
    <SlColumn widths="full">
      <SearchTextField placeholder="Search segments" value={search} onChange={handleSearchChange} />
      <SlRow horizontalPosition="center">
        {filteredSegments.length < 1 && (
          <Box marginTop={theme.spacing.xxl}>
            <NoDataPlaceholder team={team} />
          </Box>
        )}
      </SlRow>
      {filteredSegments.length > 0 && (
        <Box marginTop={theme.spacing.lg} width="100%">
          <SegmentCardGrid segments={filteredSegments} pendingSegments={processingSegments} />
        </Box>
      )}
    </SlColumn>
  );
};

export default SegmentsViewTab;
